import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { UPDATE_COMPANY_SETTINGS_MUTATION } from "../../constants/graphql";
import useAuth from "../../hooks/useAuth";
import useMutation from "../../hooks/useMutation";
import useNotification from "../../hooks/notification";

const styles = {
  dialogTitle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    padding: 2,
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
};

export default function EditCheckoutDisplayFieldLabelDialog({
  open,
  settingKey,
  initialLabel,
  onClose,
  onSaved,
}) {
  const { user, setUser } = useAuth();
  const { company } = user;
  const [updateCompanySettings, { loading }] = useMutation(UPDATE_COMPANY_SETTINGS_MUTATION);
  const { notifySuccess, notifyError } = useNotification();
  const [value, setValue] = useState(initialLabel || "");

  useEffect(() => {
    setValue(initialLabel || "");
  }, [initialLabel, settingKey, open]);

  const handleSave = async () => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    await updateCompanySettings({
      variables: {
        companySettings: {
          [settingKey]: trimmedValue,
        },
      },
      onSuccess: values => {
        setUser({
          ...user,
          company: {
            ...company,
            settings: values.company?.settings ?? company.settings,
          },
        });
        onSaved(settingKey, trimmedValue);
        notifySuccess("Checkout display field updated");
        onClose();
      },
      onFailure: notifyError,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Edit Checkout Display Field
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={styles.closeButton}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Field Name"
          fullWidth
          value={value}
          onChange={e => setValue(e.target.value)}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="primary" variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || !value.trim()}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
